import { useCallback, useEffect, useRef } from 'react';
import { useMaybeRoomContext } from '@livekit/components-react';
import { RoomEvent, type RemoteParticipant } from 'livekit-client';

/**
 * Participants who transcribe their OWN mic on their own client.
 *
 * lms-frontend's useTutorTalkTime announces itself on this LiveKit data topic
 * every 10 s while its capture is healthy. For those participants the recorder
 * must stop capturing, or every turn would be transcribed twice. Announcements
 * are trusted only from the SENDER's identity — never an identity in the
 * payload — so no one can switch off someone else's transcript.
 *
 * Staleness is the fallback: a client that stops announcing (tab crash, capture
 * failure) is picked back up by the recorder once STALE_MS passes.
 */
export const TALK_TIME_TOPIC = 'talk-time-capture';
const STALE_MS = 25000;

export const useClientCapturedParticipants = () => {
  // undefined on the Dyte path, where no client announces anything.
  const room = useMaybeRoomContext();
  const lastSeen = useRef(new Map<string, number>());

  useEffect(() => {
    if (!room) return undefined;
    const decoder = new TextDecoder();
    const onData = (
      payload: Uint8Array,
      participant?: RemoteParticipant,
      _kind?: unknown,
      topic?: string,
    ) => {
      if (topic !== TALK_TIME_TOPIC || !participant?.identity) return;
      try {
        const msg = JSON.parse(decoder.decode(payload)) as { active?: boolean };
        if (msg.active) lastSeen.current.set(participant.identity, Date.now());
        else lastSeen.current.delete(participant.identity);
      } catch {
        // malformed beat: ignore, staleness handles the rest
      }
    };
    const onLeft = (participant: RemoteParticipant) => {
      lastSeen.current.delete(participant.identity);
    };
    room.on(RoomEvent.DataReceived, onData);
    room.on(RoomEvent.ParticipantDisconnected, onLeft);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
      room.off(RoomEvent.ParticipantDisconnected, onLeft);
    };
  }, [room]);

  return useCallback((identity?: string): boolean => {
    if (!identity) return false;
    const at = lastSeen.current.get(identity);
    return at !== undefined && Date.now() - at < STALE_MS;
  }, []);
};

export default useClientCapturedParticipants;
